import React, { useMemo } from 'react';
import { Button, Col, Form, Row, Spinner } from 'react-bootstrap';
import Icon from '../../common/Icons';
import { ORIENT_LABELS, ORIENT_LABELS_SHORT, ORIENT_SERVICE } from '../receptionConstants';
import { staffRoleSuffix } from '../receptionFormatters';
import ReceptionModalShell, { ReceptionModalSection, ReceptionFormField } from '../ui/ReceptionModalShell';
import { ReceptionStatusBadge } from '../ui';

const ORIENT_LINE_OPTIONS = [
  {
    value: ORIENT_SERVICE.MEDICAL,
    label: ORIENT_LABELS[ORIENT_SERVICE.MEDICAL],
    short: ORIENT_LABELS_SHORT[ORIENT_SERVICE.MEDICAL],
    icon: 'stethoscope',
    tone: 'medical'
  },
  {
    value: ORIENT_SERVICE.DENTAL,
    label: ORIENT_LABELS[ORIENT_SERVICE.DENTAL],
    short: ORIENT_LABELS_SHORT[ORIENT_SERVICE.DENTAL],
    icon: 'user-md',
    tone: 'dental'
  },
  {
    value: ORIENT_SERVICE.NURSING,
    label: ORIENT_LABELS[ORIENT_SERVICE.NURSING],
    short: ORIENT_LABELS_SHORT[ORIENT_SERVICE.NURSING],
    icon: 'heart',
    tone: 'nursing'
  }
];

function patientInitials(name) {
  const parts = String(name || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[parts.length - 1][0] || ''}`.toUpperCase();
}

export default function IntakeOrientModal({
  show,
  onHide,
  saving = false,
  onSubmit,
  patientProfile = null,
  activeReceptionSite = null,
  defaultOrientation = ORIENT_SERVICE.DENTAL,
  serviceLine,
  onServiceLineChange,
  doctorId,
  onDoctorIdChange,
  doctorFilter,
  onDoctorFilterChange,
  orientationStaff = [],
  orientationStaffLoading = false,
  symptoms,
  onSymptomsChange
}) {
  const filteredStaff = useMemo(() => {
    const needle = doctorFilter.trim().toLowerCase();
    if (!needle) return orientationStaff;
    return orientationStaff.filter((person) => {
      const hay = `${person.name || ''} ${person.email || ''} ${person.role || ''}`.toLowerCase();
      return hay.includes(needle);
    });
  }, [orientationStaff, doctorFilter]);

  const selectedDoctorLabel = useMemo(() => {
    if (!doctorId) return 'File d\'attente du site — sans désignation';
    const person = orientationStaff.find((d) => String(d.id) === String(doctorId));
    if (!person) return 'Professionnel sélectionné';
    return `${person.name} (${staffRoleSuffix(person.role)})`;
  }, [doctorId, orientationStaff]);

  const activeLine = ORIENT_LINE_OPTIONS.find((line) => line.value === serviceLine);

  const handleServiceLineChange = (line) => {
    onServiceLineChange(line);
    onDoctorIdChange('');
    onDoctorFilterChange('');
  };

  const footer = (
    <div className="recv-intake-orient-footer">
      <Button variant="outline-secondary" className="recv-btn-ghost recv-intake-orient-footer__cancel" onClick={onHide} disabled={saving}>
        Annuler
      </Button>
      <Button variant="primary" className="recv-intake-orient-submit" onClick={onSubmit} disabled={saving}>
        {saving ? (
          <>
            <Spinner animation="border" size="sm" className="me-2" aria-hidden />
            Enregistrement…
          </>
        ) : (
          <>
            <Icon name="check" size={16} aria-hidden />
            <span className="recv-intake-orient-submit__label">Enregistrer et orienter</span>
            <span className="recv-intake-orient-submit__label-short" aria-hidden="true">
              Valider
            </span>
          </>
        )}
      </Button>
    </div>
  );

  return (
    <ReceptionModalShell
      show={show}
      onHide={onHide}
      disableClose={saving}
      scrollable
      centered
      size="xl"
      dialogClassName="recv-modal-dialog--intake-orient"
      title="Enregistrer et orienter"
      subtitle="Confirmez la filière et les informations transmises au dossier. Le passage sera visible dans la file du jour."
      footer={footer}
      bodyClassName="recv-intake-orient-modal__body"
    >
      <Row className="g-3 g-lg-4 recv-intake-orient-modal">
        <Col xs={12} lg={8} className="order-2 order-lg-1">
          {patientProfile ? (
            <div className="recv-intake-orient-patient mb-4">
              <div className="recv-intake-orient-patient__avatar" aria-hidden="true">
                {patientInitials(patientProfile.displayName)}
              </div>
              <div className="recv-intake-orient-patient__content">
                <span className="recv-intake-orient-patient__eyebrow">Patient identifié</span>
                <div className="recv-intake-orient-patient__name">{patientProfile.displayName}</div>
                {patientProfile.details?.length ? (
                  <div className="recv-intake-orient-patient__meta">{patientProfile.details.join(' · ')}</div>
                ) : null}
              </div>
            </div>
          ) : null}

          <ReceptionModalSection
            className="recv-modal-section--tone-site"
            title="Site d'accueil"
            description="Le passage est enregistré sur votre site de travail actif."
          >
            <div className="recv-intake-orient-site">
              <span className="recv-intake-orient-site__icon" aria-hidden="true">
                <Icon name="building" size={18} />
              </span>
              <div className="recv-intake-orient-site__text">
                <span className="recv-intake-orient-site__name">
                  {activeReceptionSite?.name || 'Site non défini'}
                </span>
                <span className="recv-intake-orient-site__hint">Site actif pour cette session</span>
              </div>
              <span className="recv-intake-orient-site__badge">Accueil</span>
            </div>
          </ReceptionModalSection>

          <ReceptionModalSection
            className="recv-modal-section--tone-line"
            title="Filière d'orientation"
            description={`Par défaut : ${ORIENT_LABELS[defaultOrientation] || defaultOrientation}. Choisissez la filière de prise en charge.`}
          >
            <div className="recv-orient-line-grid" role="radiogroup" aria-label="Filière d'orientation">
              {ORIENT_LINE_OPTIONS.map((line) => {
                const isActive = serviceLine === line.value;
                return (
                  <button
                    key={line.value}
                    type="button"
                    role="radio"
                    aria-checked={isActive}
                    className={`recv-orient-line-card recv-orient-line-card--${line.tone}${isActive ? ' is-active' : ''}`}
                    onClick={() => handleServiceLineChange(line.value)}
                  >
                    <span className="recv-orient-line-card__icon" aria-hidden="true">
                      <Icon name={line.icon} size={20} />
                    </span>
                    <span className="recv-orient-line-card__label">{line.short}</span>
                    <span className="recv-orient-line-card__hint">{line.label}</span>
                  </button>
                );
              })}
            </div>
          </ReceptionModalSection>

          <ReceptionModalSection
            className="recv-modal-section--tone-staff"
            title="Professionnel"
            description="Facultatif. Sans sélection, le patient rejoint la file d'attente générale du site."
          >
            <ReceptionFormField
              label="Recherche"
              htmlFor="intake-orient-doctor-filter"
              hint="Filtrer par nom, rôle ou e-mail"
            >
              <Form.Control
                id="intake-orient-doctor-filter"
                type="search"
                value={doctorFilter}
                onChange={(ev) => onDoctorFilterChange(ev.target.value)}
                placeholder="Nom du professionnel…"
                autoComplete="off"
              />
            </ReceptionFormField>

            {orientationStaffLoading ? (
              <div className="recv-intake-orient-staff-loading">
                <Spinner animation="border" size="sm" variant="primary" aria-hidden />
                <span>Chargement des professionnels…</span>
              </div>
            ) : (
              <ReceptionFormField label="Assignation" htmlFor="intake-orient-doctor">
                <Form.Select
                  id="intake-orient-doctor"
                  value={doctorId}
                  onChange={(ev) => onDoctorIdChange(ev.target.value)}
                >
                  <option value="">File d&apos;attente — sans désignation</option>
                  {filteredStaff.map((person) => (
                    <option key={person.id} value={person.id}>
                      {person.name} — {staffRoleSuffix(person.role)}
                      {person.email ? ` (${person.email})` : ''}
                    </option>
                  ))}
                </Form.Select>
              </ReceptionFormField>
            )}

            {!orientationStaffLoading && filteredStaff.length === 0 ? (
              <p className="recv-modal-empty-inline mb-0">
                Aucun professionnel disponible pour {activeLine?.label || 'cette filière'}.
              </p>
            ) : null}
          </ReceptionModalSection>

          <ReceptionModalSection
            className="recv-modal-section--tone-motif mb-0"
            title="Motif transmis au dossier"
          >
            <ReceptionFormField
              label="Symptômes ou motif de venue"
              htmlFor="intake-orient-symptoms"
              hint="Visible sur la consultation en attente (optionnel)"
            >
              <Form.Control
                id="intake-orient-symptoms"
                as="textarea"
                rows={4}
                value={symptoms}
                onChange={(ev) => onSymptomsChange(ev.target.value)}
                placeholder="Ex. fièvre depuis ce matin, douleur dentaire, contrôle de routine…"
              />
            </ReceptionFormField>
          </ReceptionModalSection>
        </Col>

        <Col xs={12} lg={4} className="order-1 order-lg-2">
          <aside className="recv-modal-summary recv-intake-orient-summary recv-intake-orient-summary--recv" aria-label="Résumé du passage">
            <div className="recv-intake-orient-summary__head">
              <span className="recv-intake-orient-summary__chip">Aperçu</span>
              <h3 className="recv-modal-summary__title mb-0">Résumé</h3>
            </div>
            <ul className="recv-modal-summary__list">
              <li>
                <span className="recv-modal-summary__key">Patient</span>
                <span className="recv-modal-summary__val">
                  {patientProfile?.displayName || <span className="text-muted">Non identifié</span>}
                </span>
              </li>
              <li>
                <span className="recv-modal-summary__key">Site</span>
                <span className="recv-modal-summary__val">
                  {activeReceptionSite?.name || '—'}
                </span>
              </li>
              <li>
                <span className="recv-modal-summary__key">Filière</span>
                <span className="recv-modal-summary__val">
                  {activeLine ? (
                    <span className={`recv-service-line recv-service-line--compact recv-service-line--${activeLine.tone}`}>
                      {activeLine.label}
                    </span>
                  ) : (
                    '—'
                  )}
                </span>
              </li>
              <li>
                <span className="recv-modal-summary__key">Professionnel</span>
                <span className="recv-modal-summary__val">{selectedDoctorLabel}</span>
              </li>
              <li>
                <span className="recv-modal-summary__key">Consultation</span>
                <span className="recv-modal-summary__val">
                  <ReceptionStatusBadge kind="consultation" value="PENDING" />
                </span>
              </li>
              {symptoms.trim() ? (
                <li>
                  <span className="recv-modal-summary__key">Motif</span>
                  <span className="recv-modal-summary__val recv-intake-orient-summary__motif">{symptoms.trim()}</span>
                </li>
              ) : null}
            </ul>
            <p className="recv-intake-orient-summary__note mb-0">
              Une fois validé, le passage apparaît immédiatement dans la file orientée du site.
            </p>
          </aside>
        </Col>
      </Row>
    </ReceptionModalShell>
  );
}
